import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// Canonical production origin - social crawlers, Googlebot and AI crawlers
// all resolve absolute URLs from here regardless of which environment they
// happen to hit. Keep in sync with the deployed domain.
export const SITE_ORIGIN = "https://whyhighwaysindustry.com";
const DEFAULT_TITLE = "HighwaysIndustry.com - Create. Amplify. Position.";
const DEFAULT_DESCRIPTION =
  "Content, media, video and brand-building built for the UK highways and infrastructure sector. HighwaysIndustry.com helps businesses become recognised, remembered and respected.";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

/**
 * Drop-in SEO block for every page. Renders full title/description, canonical
 * URL, Open Graph, Twitter card, and (optional) JSON-LD structured data.
 *
 * Every prop is optional and falls back to the site-wide defaults.
 */
export default function Seo({
  title,
  description,
  image,
  path,
  type = "website",
  noindex = false,
  jsonLd,
  keywords,
}) {
  const { pathname } = useLocation();
  const routePath = path || pathname || "/";
  const url = `${SITE_ORIGIN}${routePath}`;
  const fullTitle = title
    ? `${title} - HighwaysIndustry.com`
    : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  const img = image ? (image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`) : DEFAULT_OG_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="HighwaysIndustry.com" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      <meta name="twitter:site" content="@highwaysindustry" />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
